import { computed, h, unref, watch } from 'vue';

import { useState } from '@components/composable';
import { classNames, omit } from '@components/utils';

import { CdsPagination } from '../../CdsPagination';
import { CdsDropdown } from '../../CdsDropdown';

import type { NTable } from '../interface';
import type { Ref, VNode } from 'vue';

export const DEFAULT_PAGE_SIZE = 10; // @todo вынести в настройки библиотеки
export const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 30, 50]; // @todo вынести в настройки библиотеки
export const DEFAULT_PAGINATION_POSITION = 'right'; // @todo вынести в настройки библиотеки

export interface IUsePagination {
  mergedPagination: Ref<NTable.NPagination.IProps>;
  resetPagination: (modelValue?: number, length?: number) => void;
}

export function usePagination(
  totalRef: Ref<number>,
  paginationRef: Ref<NTable.NPagination.IProps | false | undefined>,
  disabled: Ref<boolean>,
  onChange: (modelValue: number | undefined, length: number) => void
): IUsePagination {
  const pagination = computed(() =>
    paginationRef.value && typeof paginationRef.value === 'object' ? paginationRef.value : {}
  );
  const paginationTotal = computed(() => pagination.value.total || 0);

  const [innerPagination, setInnerPagination] = useState<{ modelValue?: number; total?: number }>(() => ({
    modelValue: 'modelValue' in pagination.value ? pagination.value.modelValue! : 1,
    total: paginationTotal.value || DEFAULT_PAGE_SIZE
  }));

  watch(paginationTotal, (value) => {
    setInnerPagination({
      ...innerPagination.value,
      total: value || DEFAULT_PAGE_SIZE
    });
  });

  const mergedPagination = computed(() => {
    const result = extendsObject<Partial<NTable.NPagination.IProps>>(innerPagination.value, pagination.value, {
      length: pagination.value.length || Math.ceil(totalRef.value / innerPagination.value.total!),
      disabled: disabled.value
    });

    const maxPage = Math.ceil(((paginationTotal.value as number) || totalRef.value) / (result.length as number)!);

    if (result.modelValue! > (result.length as number)) {
      result.modelValue = maxPage || 1;
    }

    return result;
  });

  const resetPagination = (modelValue?: number, total?: number) => {
    if ((pagination.value as unknown) === false) return;

    setInnerPagination({
      modelValue: modelValue ?? 1,
      total: total ?? (mergedPagination.value.total as number)
    });
  };

  const onInternalChange: NTable.NPagination.IProps['onUpdate:modelValue'] = (modelValue: number | undefined) => {
    if (pagination.value) {
      pagination.value.modelValue = modelValue;
    }
    resetPagination(modelValue, innerPagination.value.total);
    onChange(modelValue, mergedPagination.value.length as number);
  };

  return {
    mergedPagination: computed(() =>
      (pagination.value as unknown) === false
        ? {}
        : { ...mergedPagination.value, 'onUpdate:modelValue': onInternalChange }
    ),
    resetPagination
  };
}

export function getPaginationParam(
  pagination: NTable.NPagination.IProps | boolean | undefined,
  mergedPagination: NTable.NPagination.IProps
) {
  const param: any = {
    current: mergedPagination.modelValue,
    length: mergedPagination.length
  };
  const paginationObj = pagination && typeof pagination === 'object' ? pagination : {};

  Object.keys(paginationObj).forEach((pageProp) => {
    const value = (mergedPagination as any)[pageProp];

    if (typeof value !== 'function') {
      param[pageProp] = value;
    }
  });

  return param;
}

export function useRenderPagination(
  paginationProps: Ref<NTable.NPagination.IProps>,
  pagination?: false | NTable.NPagination.IProps,
  tableLocale?: Ref<NTable.ILocale>,
  triggerOnChange?: (info: Partial<NTable.IChangeEventInfo>, action: NTable.TAction, reset: boolean) => void
) {
  let topPaginationNode: VNode | undefined = undefined;
  let bottomPaginationNode: VNode | undefined = undefined;

  if (pagination !== false && paginationProps.value?.total) {
    const renderPagination = (position: string) => {
      const showPaginationCounter =
        unref(paginationProps).counter && unref(paginationProps).length && unref(paginationProps).total;
      const showPaginationSize = unref(paginationProps).size;
      const pageSizeOptions = unref(paginationProps).pageSizeOptions || DEFAULT_PAGE_SIZE_OPTIONS;
      const pageSizeItems = Array.from(new Set([...pageSizeOptions]), (item) => ({
        value: item,
        title: item.toString()
      }));

      return h('div', { class: classNames('cds-table__pagination', `cds-table__pagination--${position}`) }, [
        h(CdsPagination, {
          ...omit(unref(paginationProps), ['counter', 'size', 'position', 'total', 'pageSizeOptions'])
        }),
        showPaginationCounter
          ? h('div', { class: 'cds-table__pagination-counter' }, [
              h('span', {}, [
                `${unref(tableLocale)?.showed} `,
                h('strong', {}, [unref(paginationProps).total]),
                ` ${unref(tableLocale)?.from} `,
                h('strong', {}, [(unref(paginationProps).length as number) * unref(paginationProps)!.total!])
              ])
            ])
          : null,
        showPaginationSize &&
          h('div', { class: 'cds-table__pagination-size' }, [
            h('span', {}, `${unref(tableLocale)?.rowOnPage}:`),
            h(CdsDropdown, {
              hideMessages: true,
              items: pageSizeItems,
              modelValue: unref(paginationProps).total,
              'onUpdate:modelValue': (pageSize: number) => {
                triggerOnChange?.(
                  {
                    pagination: {
                      total: pageSize,
                      modelValue: unref(paginationProps).modelValue,
                      length: unref(paginationProps).length as number
                    }
                  },
                  'paginate',
                  false
                );
              }
            })
          ])
      ]);
    };

    const { position } = paginationProps.value;

    const defaultPosition = DEFAULT_PAGINATION_POSITION;
    if (position !== null && Array.isArray(position)) {
      const topPos = position.find((p) => p.indexOf('top') !== -1);
      const bottomPos = position.find((p) => p.indexOf('bottom') !== -1);
      const isDisabled = position.every((p) => `${p}` === 'none');

      if (!topPos && !bottomPos && !isDisabled) {
        bottomPaginationNode = renderPagination(defaultPosition);
      }

      if (topPos) {
        topPaginationNode = renderPagination(topPos!.toLowerCase().replace('top', ''));
      }

      if (bottomPos) {
        bottomPaginationNode = renderPagination(bottomPos!.toLowerCase().replace('bottom', ''));
      }
    } else {
      bottomPaginationNode = renderPagination(defaultPosition);
    }
  }

  return { topPaginationNode, bottomPaginationNode };
}

function extendsObject<T extends object>(...list: T[]) {
  const result: T = {} as T;

  list.forEach((obj) => {
    if (obj) {
      Object.keys(obj).forEach((key) => {
        const val = (obj as any)[key];
        if (val !== undefined) {
          (result as any)[key] = val;
        }
      });
    }
  });

  return result;
}
